import type { ActionDescriptor, TestRef } from '@krp/client-runtime'

import { AQSI_WINDOWS as W } from '../windows/registerWindows'

/**
 * Записи действий потока РМК пакета `resource-aqsi-ui` (UI-ACTIONS, UI-RMK-FLOW 3.13, RP-FB22): что делает каждая кнопка
 * потока «Начало работы» → ожидание → порог смены → продажа — для подсказки ⓘ. Остальные окна пакета (каталог, чеки,
 * возврат, инциденты) описывает их карточка UI-ACTIONS-2c; пока она не влита, правило ⓘ включено только на экранах потока
 * (`features/rmk/ui/flow`), а полный реестр пакета (`registerActions.ts`) не объявлен. Описания — словами магазина, без
 * кодов и внутренних слов (это проверяет тест реестра); статус вычисляется из доказательств. Тест — путь от корня
 * репозитория и ТОЧНОЕ имя: тест реестра находит каждый, иначе CI красный.
 */
const PKG = 'frontend/packages/resource-aqsi-ui/src/features'
const WEB = 'frontend/apps/web/src/tests'
const SRV = 'tests/Resource.Aqsi'

const unit = (file: string, name: string): TestRef => ({ file, name })
const pkg = (file: string, name: string): TestRef => unit(`${PKG}/${file}`, name)
const web = (file: string, name: string): TestRef => unit(`${WEB}/${file}`, name)
const srv = (file: string, name: string): TestRef => unit(`${SRV}/${file}`, name)

const FLOW = 'rmk/tests/RmkFlow.test.tsx'
const START = 'rmk/tests/StartFlow.test.tsx'
const START_STORE = 'rmk/tests/rmkStart.store.test.ts'
const OTHER_TAB = 'rmk/tests/otherTab.test.tsx'
const STORE = 'rmk/tests/rmkFlow.store.test.ts'
const OWN = 'rmk/tests/ownSignIn.test.tsx'
const RIGHT_TAKEOVER = 'Тот, кому выдано право садиться за кассу вместо другого или закрывать чужую работу.'

export const AQSI_ACTIONS: readonly ActionDescriptor[] = [
  {
    id: 'aqsi.rmk.reload',
    windows: [W.rmk],
    label: 'Обновить',
    summary: 'Заново читает кассы, кассиров и смены из кабинета AQSI. Кассе при этом ничего не отправляется.',
    effect: 'read-external',
    evidence: { unit: [pkg(START, 'Cabinet_failure_says_the_list_is_not_shown_and_offers_retry')] },
  },
  {
    id: 'aqsi.rmk.choose-cashier',
    windows: [W.rmk],
    label: 'Кассир',
    summary:
      'Выбираете кассира кабинета, под которым будете работать: его имя попадёт в чеки и в смену. Ничего не отправляется — выбор запоминается на этом устройстве.',
    effect: 'view',
    evidence: {
      unit: [
        pkg(START, 'Remembered_cashier_is_selected_and_marked_as_last_time'),
        pkg(START, 'Cashiers_are_searchable_only_above_eight'),
        pkg(START_STORE, 'Back_before_open_creates_nothing_on_the_server'),
      ],
    },
  },
  {
    id: 'aqsi.rmk.own-name',
    windows: [W.rmk],
    label: 'Работать под своим именем',
    summary:
      'Нужна, когда в кабинете нет ни одного кассира: чеки и смена пойдут на имя вашей учётной записи. Ничего не отправляется.',
    effect: 'view',
    evidence: { unit: [pkg(START, 'No_cashiers_offers_own_name_with_explanation')] },
  },
  {
    id: 'aqsi.rmk.verify-code',
    windows: [W.rmk],
    label: 'Проверить',
    summary:
      'Отправляет введённый код нашей службе: она сверяет его с кабинетом и выдаёт подтверждение на несколько минут. Сам код нигде не сохраняется. После нескольких неверных кодов кассир блокируется на время.',
    effect: 'write-local',
    disabledWhen: 'Код не введён, идёт проверка или кассир заблокирован.',
    evidence: {
      unit: [
        pkg(START, 'Code_field_is_hidden_numeric_and_not_autofilled_and_enter_submits'),
        pkg(START_STORE, 'Wrong_code_keeps_attempts_left_and_never_stores_the_code'),
        pkg(START_STORE, 'Locked_answer_blocks_with_a_countdown_and_survives_refresh'),
      ],
    },
  },
  {
    id: 'aqsi.rmk.own-cashier',
    windows: [W.rmk],
    label: 'Кассир',
    summary:
      'Кассиры из вкладки «Кассиры», связанные с кассиром этого кабинета. Выберите себя — дальше нужен только ваш код. Ничего не отправляется.',
    effect: 'view',
    evidence: { unit: [pkg(OWN, 'Own_cashiers_of_the_cabinet_are_listed_and_the_code_is_asked_for_the_chosen_one')] },
  },
  {
    id: 'aqsi.rmk.own-sign-in',
    windows: [W.rmk],
    label: 'Войти',
    summary:
      'Отправляет код выбранного кассира нашей службе: она проверяет его и выдаёт подтверждение на несколько минут для работы на этой кассе. Открытая смена продолжается. Сам код нигде не сохраняется; после нескольких неверных кодов вход с этого места блокируется на время.',
    effect: 'write-local',
    disabledWhen: 'Кассир не выбран, код не введён, идёт проверка или вход заблокирован.',
    evidence: {
      unit: [
        pkg(OWN, 'Own_code_is_sent_once_with_the_chosen_cashier_and_the_field_is_cleared'),
        pkg(OWN, 'Refusal_is_said_in_the_service_words_and_the_code_is_not_kept'),
      ],
    },
  },
  {
    id: 'aqsi.rmk.move-here',
    windows: [W.rmk],
    label: 'Перенести сюда',
    summary:
      'Рабочее место кассы открыто в другой вкладке. Кнопка отправляет код кассира нашей службе: если он верный, работа переходит в эту вкладку, а в прежней появляется такая же подсказка. Сам код нигде не сохраняется.',
    effect: 'write-local',
    disabledWhen: 'Код не введён или идёт проверка.',
    evidence: {
      unit: [pkg(OTHER_TAB, 'Other_tab_screen_sends_nothing_without_the_code_and_moves_the_workplace_with_it')],
    },
  },
  {
    id: 'aqsi.rmk.change-step',
    windows: [W.rmk],
    label: 'изменить',
    summary: 'Раскрывает выбранный шаг, чтобы выбрать другое. Ничего не отправляется.',
    effect: 'view',
    evidence: { unit: [pkg(START, 'Start_stage_shows_cashier_first_and_opens_next_blocks_as_each_is_ready')] },
  },
  {
    id: 'aqsi.rmk.choose-register',
    windows: [W.rmk],
    label: 'Касса',
    summary:
      'Выбираете кассу, за которой будете работать: свободную или такую, где смена уже открыта и за ней никто не работает. Занятая недоступна. Ничего не отправляется.',
    effect: 'view',
    evidence: {
      unit: [
        pkg(START, 'Single_free_register_is_chosen_by_itself'),
        pkg(START, 'Open_shift_of_register_is_offered_as_continue_shift'),
        pkg(START, 'Occupied_register_is_unavailable_with_who_and_since'),
      ],
    },
  },
  {
    id: 'aqsi.rmk.continue-shift',
    windows: [W.rmk],
    label: 'Продолжить смену',
    summary:
      'Вы садитесь за кассу под выбранным кассиром и продолжаете смену, которая уже открыта. Кассе ничего не отправляется — смена принимается в программе.',
    effect: 'write-local',
    disabledWhen: 'Не выбран кассир, не введён код, не выбрана касса или идёт другая команда.',
    evidence: {
      unit: [
        pkg(START_STORE, 'Continue_shift_sits_down_with_the_chosen_cashier_and_adopts_the_shift'),
        pkg(START, 'Open_shift_of_register_is_offered_as_continue_shift'),
      ],
      server: [srv('Pos/AqsiRegisterSessionTests.cs', 'Two_people_on_one_register_one_wins_and_one_person_on_two_registers_is_refused')],
    },
  },
  {
    id: 'aqsi.rmk.open-shift',
    windows: [W.rmk],
    label: 'Открыть смену',
    summary:
      'Одним действием: проверим связь с кассой, посадим вас за кассу под выбранным кассиром с выбранным видом цены и откроем на ней смену — касса получит команду. Подтвердите её на экране кассы, если она спросит.',
    effect: 'cash',
    disabledWhen: 'Не выбран кассир, не введён код кассира, не выбрана касса или идёт другая команда.',
    evidence: {
      unit: [
        pkg(START_STORE, 'Start_press_sends_one_session_open_with_cashier_proof_and_price_type'),
        pkg(START_STORE, 'Double_press_sends_one_command'),
        pkg(START_STORE, 'Expired_proof_is_not_sent_and_asks_for_the_code_again_keeping_the_choice'),
        pkg(STORE, 'Open_shift_sends_session_open_and_shift_open_by_device'),
        pkg(STORE, 'Open_shift_checks_connection_first_when_register_is_not_online'),
        pkg(STORE, 'Open_shift_is_not_sent_when_register_does_not_answer'),
        pkg(START, 'Open_shift_is_disabled_with_the_reason_in_words_until_everything_is_chosen'),
      ],
      server: [srv('Pos/AqsiRegisterSessionTests.cs', 'Register_missing_from_the_cabinet_or_in_another_shop_is_refused_with_words_before_any_post')],
    },
  },
  {
    id: 'aqsi.rmk.check-device',
    windows: [W.rmk],
    label: 'Проверить связь',
    summary:
      'Спрашивает, отвечают ли кабинет AQSI и сама касса (одна служебная команда кассе, включена по умолчанию; выключенная настройкой — аппарат не проверяется). Итог виден в точке «Связь» в шапке. После зависшей оплаты служба делает такую проверку сама.',
    effect: 'read-external',
    disabledWhen: 'Уже идёт другая команда.',
    evidence: {
      unit: [pkg(STORE, 'Link_check_posts_once_and_reports_both_levels'), pkg(FLOW, 'Not_checked_device_is_neutral_not_an_error_and_the_check_button_asks_the_service')],
      server: [srv('Devices/AqsiRegisterLinkTests.cs', 'Probe_is_disabled_by_default_and_says_not_checked_instead_of_no_link')],
    },
  },
  {
    id: 'aqsi.rmk.check-shift',
    windows: [W.rmk],
    label: 'Проверить',
    summary:
      'Читает у кабинета, открыта ли смена на кассе, и спрашивает кассу, отвечает ли она. Кассе ничего не печатается.',
    effect: 'read-external',
    disabledWhen: 'Уже идёт проверка.',
    evidence: { unit: [pkg(FLOW, 'Shift_gate_has_one_check_button_and_no_price_type')] },
  },
  {
    id: 'aqsi.rmk.release',
    windows: [W.rmk],
    label: 'Освободить…',
    summary: 'Открывает окно, где администратор освобождает кассу, за которой работает другой сотрудник. Причина обязательна.',
    effect: 'view',
    access: RIGHT_TAKEOVER,
    evidence: { unit: [pkg(START, 'Release_is_offered_only_with_takeover_or_close_any_right')] },
  },
  {
    id: 'aqsi.rmk.takeover',
    windows: [W.rmk],
    label: 'Сесть за кассу самому',
    summary: 'Работа за кассой переходит к вам, смена на кассе не закрывается. Причина пишется в журнал кассы.',
    effect: 'write-local',
    access: RIGHT_TAKEOVER,
    disabledWhen: 'Не написана причина или служба не назвала, чью работу передавать.',
    evidence: { unit: [pkg(START, 'Release_is_offered_only_with_takeover_or_close_any_right')] },
  },
  {
    id: 'aqsi.rmk.close-foreign',
    windows: [W.rmk],
    label: 'Закончить чужую работу',
    summary: 'Освобождает кассу без смены. Можно, только если смена закрыта и нет незавершённых продаж.',
    effect: 'write-local',
    access: RIGHT_TAKEOVER,
    disabledWhen: 'Не написана причина или служба не назвала, чью работу закрывать.',
    evidence: { unit: [pkg(START, 'Release_is_offered_only_with_takeover_or_close_any_right')] },
  },
  {
    id: 'aqsi.rmk.journal',
    windows: [W.rmk],
    label: 'Журнал кассы',
    summary: 'Показывает, кто и когда садился за эту кассу и забирал её.',
    effect: 'read',
    access: 'Тот, кому выдано право смотреть журнал кассы.',
    evidence: { unit: [pkg(START, 'Release_is_offered_only_with_takeover_or_close_any_right')] },
  },
  {
    id: 'aqsi.rmk.close-dialog',
    windows: [W.rmk],
    label: 'Закрыть',
    summary: 'Закрывает окно «Журнал кассы». Ничего не меняет.',
    effect: 'view',
    evidence: { unit: [pkg(START, 'Release_is_offered_only_with_takeover_or_close_any_right')] },
  },
  {
    id: 'aqsi.rmk.cancel-dialog',
    windows: [W.rmk],
    label: 'Отмена',
    summary: 'Закрывает окно «Освободить кассу», ничего не меняя.',
    effect: 'view',
    evidence: { unit: [pkg(START, 'Release_is_offered_only_with_takeover_or_close_any_right')] },
  },
  {
    id: 'aqsi.rmk.other-source',
    windows: [W.rmk],
    label: 'Другой источник',
    summary: 'Возвращает к выбору источника. Ваша работа за кассой остаётся открытой.',
    effect: 'view',
    evidence: {
      unit: [
        pkg(FLOW, 'Other_source_in_actions_goes_back_to_the_tiles'),
        web('rmkSources.test.tsx', 'Returning_to_rmk_from_inside_shows_tiles_without_jump'),
      ],
    },
  },
]
